import React from 'react';
import { getInterestRate } from '../../Succession/Financing/financingCalculations';

const FinancingInputCard = ({
  purchasePrice,
  setPurchasePrice,
  downPaymentMode,
  setDownPaymentMode,
  downPaymentDollar,
  setDownPaymentDollar,
  downPaymentPercent,
  setDownPaymentPercent,
  loanTerm,
  setLoanTerm,
  selectedLenderId,
  onSelectLender,
  customRate,
  setCustomRate,
  lenders,
  loanAmount,
  showValuationNote = false,
}) => {
  const selectedLender = lenders.find((l) => String(l.id) === String(selectedLenderId));

  const getLenderDisplayRate = (lender) => {
    const rate = getInterestRate(lender, loanAmount);
    return (rate * 100).toFixed(1);
  };

  const placeholderRate = selectedLender
    ? getLenderDisplayRate(selectedLender)
    : '7.2';

  return (
    <div className="bg-card border border-border rounded-[16px] p-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
        <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
          DEAL PARAMETERS
        </span>
      </div>

      <div className="space-y-6">
        {/* Purchase Price */}
        <div>
          <label className="block text-[13px] text-muted-foreground mb-2">
            Business Purchase Price
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <input
              type="text"
              value={purchasePrice}
              onChange={(e) =>
                setPurchasePrice(e.target.value.replace(/[^0-9]/g, ''))
              }
              placeholder="500,000"
              className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
            />
          </div>
          {showValuationNote && (
            <p className="font-mono text-[11px] text-muted-foreground mt-2">
              Based on your most recent valuation estimate
            </p>
          )}
        </div>

        {/* Down Payment */}
        <div>
          <label className="block text-[13px] text-muted-foreground mb-2">
            Down Payment
          </label>
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <input
                type="text"
                value={downPaymentDollar}
                onChange={(e) => {
                  setDownPaymentDollar(e.target.value.replace(/[^0-9]/g, ''));
                  setDownPaymentMode('dollar');
                }}
                placeholder="125,000"
                className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
              />
            </div>
            <span className="text-[13px] text-muted-foreground">or</span>
          </div>
          <div className="flex items-center gap-2">
            {[25, 30, 40].map((percent) => (
              <button
                key={percent}
                onClick={() => {
                  setDownPaymentPercent(percent);
                  setDownPaymentMode('percent');
                }}
                className={`flex-1 font-mono text-[11px] uppercase px-4 py-2 transition-colors ${
                  downPaymentMode === 'percent' && downPaymentPercent === percent
                    ? 'bg-foreground text-background rounded-full'
                    : 'border border-border text-muted-foreground rounded-full hover:border-foreground/30'
                }`}
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>

        {/* Loan Term */}
        <div>
          <label className="block text-[13px] text-muted-foreground mb-2">
            Loan Term
          </label>
          <div className="flex items-center gap-2">
            {[3, 5, 7, 10].map((term) => (
              <button
                key={term}
                onClick={() => setLoanTerm(term)}
                className={`flex-1 font-mono text-[11px] uppercase px-4 py-2 transition-colors ${
                  loanTerm === term
                    ? 'bg-foreground text-background rounded-full'
                    : 'border border-border text-muted-foreground rounded-full hover:border-foreground/30'
                }`}
              >
                {term} YRS
              </button>
            ))}
          </div>
        </div>

        {/* Interest Rate */}
        <div>
          <label className="block text-[13px] text-muted-foreground mb-2">
            Interest Rate
          </label>
          <div className="relative">
            <input
              type="text"
              value={customRate}
              onChange={(e) =>
                setCustomRate(e.target.value.replace(/[^0-9.]/g, ''))
              }
              placeholder={placeholderRate}
              className="w-full bg-muted border border-input rounded-[8px] px-4 pr-8 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
              %
            </span>
          </div>
          <p className="font-mono text-[11px] text-muted-foreground mt-2">
            Based on current lender benchmarks
          </p>
        </div>
      </div>

      {/* Lender Selector */}
      <div className="mt-6 bg-muted/50 border border-border rounded-[12px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            SELECT LENDER (OPTIONAL)
          </span>
        </div>
        <div className="space-y-3">
          {lenders.map((lender) => (
            <button
              key={lender.id}
              onClick={() => onSelectLender(lender.id)}
              className="w-full flex items-center gap-3 text-left hover:bg-background/50 rounded-[8px] p-2 transition-colors"
            >
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  String(selectedLenderId) === String(lender.id)
                    ? 'border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]'
                    : 'border-border'
                }`}
              >
                {String(selectedLenderId) === String(lender.id) && (
                  <div className="w-2 h-2 rounded-full bg-background" />
                )}
              </div>
              {lender.small_logo_url && (
                <img src={lender.small_logo_url} alt={lender.name} className="w-6 h-6 object-contain" />
              )}
              <span className="text-[14px] text-foreground flex-1">
                {lender.name}
              </span>
              <span className="font-mono text-[11px] bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1">
                {getLenderDisplayRate(lender)}%
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinancingInputCard;
